import type { GlobalDialogDataDefinition, GlobalDialogType } from '../global-dialogs.store.types';

import { useMemo } from 'react';
import { getDialogKeyByPageContext } from 'shared/legacy/contexts/DialogContext/getDialogKeyByPageContext';
import { getPageContext } from 'utils/getPageContext';
import { useGlobalDialogsStore } from '../global-dialogs.store';
import { useGlobalDialogKey } from './useGlobalDialogKey';

type UseGlobalDialogDataProps<T extends GlobalDialogType> = {
  isVisible?: boolean;
  dialogType: T;
  key?: string;
};

/**
 * Hook for accessing global dialog data with type safety and automatic key resolution.
 *
 * This hook provides access to dialog data stored in the global dialogs store. It supports
 * both direct data access and optimized selector-based access for performance. The hook
 * automatically resolves the dialog key based on page context and active dialog state.
 *
 * @template T - The specific GlobalDialogType to access data for
 *
 * @param props - Configuration object containing:
 * - `dialogType`: The dialog type to get data for
 * - `key`: Optional specific key to use (defaults to active dialog key)
 * - `isVisible`: Whether the dialog is visible (defaults to true, returns null if false)
 *
 * @returns The dialog data for the specified type, or null if not found/not visible
 *
 * @example
 * **Basic usage** - Getting dialog data:
 * ```tsx
 * const candidateData = useGlobalDialogData({
 *   dialogType: GlobalDialogType.CandidateOutreach
 * });
 *
 * if (candidateData) {
 *   console.log(candidateData.candidate.name);
 *   console.log(candidateData.outreachType);
 * }
 * ```
 *
 * @example
 * **With custom key** - Getting data for a specific dialog instance:
 * ```tsx
 * const campaignData = useGlobalDialogData({
 *   dialogType: GlobalDialogType.CampaignPhoneReachOut,
 *   key: 'campaign-123'
 * });
 * ```
 *
 * @example
 * **With visibility control** - Conditionally accessing data:
 * ```tsx
 * const dialogData = useGlobalDialogData({
 *   dialogType: GlobalDialogType.CandidateOutreach,
 *   isVisible: isDialogOpen // Returns null when false
 * });
 * ```
 */
export function useGlobalDialogData<T extends GlobalDialogType>(
  props: UseGlobalDialogDataProps<T>,
): GlobalDialogDataDefinition[T] | null;

/**
 * Hook for accessing global dialog data with type safety and performance optimization.
 *
 * This overload provides a selector function for performance optimization, allowing you
 * to subscribe to only specific parts of the dialog data and transform it as needed.
 *
 * @template T - The specific GlobalDialogType to access data for
 * @template R - The return type of the selector function
 *
 * @param props - Configuration object (same as base overload)
 * @param selector - Function to select and transform specific data from the dialog state
 *
 * @returns The result of the selector function, or null if dialog data not found/not visible
 *
 * @example
 * **With selector for performance** - Getting only specific data:
 * ```tsx
 * const candidateName = useGlobalDialogData(
 *   { dialogType: GlobalDialogType.CandidateOutreach },
 *   (data) => data.candidate.name
 * );
 *
 * // Only re-renders when candidate name changes, not when outreachType changes
 * ```
 *
 * @example
 * **Complex selector** - Transforming data:
 * ```tsx
 * const candidateInfo = useGlobalDialogData(
 *   { dialogType: GlobalDialogType.CandidateOutreach },
 *   (data) => ({
 *     displayName: `${data.candidate.firstName} ${data.candidate.lastName}`,
 *     isEmailOutreach: data.outreachType === OutreachType.Email
 *   })
 * );
 * ```
 *
 * @remarks
 * **Key Resolution**:
 * - If no key is provided, uses the active dialog key from the store
 * - Keys are resolved using page context (same logic as `useGlobalDialog`)
 * - For campaign/search dialogs, key is typically the campaign/search ID
 * - For page-specific dialogs, key is derived from page context
 *
 * **Performance**:
 * - Use the selector overload when you only need specific parts of the data
 * - The selector function is memoized and only re-runs when dialog data changes
 * - Setting `isVisible: false` prevents unnecessary data retrieval
 *
 * **Type Safety**:
 * - Return type is automatically inferred from the dialog type
 * - Selector function receives properly typed dialog data
 * - Returns null when dialog data doesn't exist or isn't visible
 *
 * @see {@link useGlobalDialog} for opening and closing dialogs
 * @see {@link useGlobalDialogKey} for getting active dialog keys
 * @see {@link useIsGlobalDialogOpen} for checking dialog visibility
 */
export function useGlobalDialogData<T extends GlobalDialogType, R>(
  props: UseGlobalDialogDataProps<T>,
  selector: (state: GlobalDialogDataDefinition[T]) => R,
): R | null;

export function useGlobalDialogData<T extends GlobalDialogType, R = GlobalDialogDataDefinition[T] | null>(
  { isVisible = true, dialogType, key: keyProp }: UseGlobalDialogDataProps<T>,
  selector?: (state: GlobalDialogDataDefinition[T]) => R,
) {
  const activeDialogKey = useGlobalDialogKey(dialogType);
  const dialogData = useGlobalDialogsStore((state) => {
    const key = getDialogKeyByPageContext(dialogType, getPageContext(), keyProp ?? activeDialogKey ?? 'fallback-key');

    if (!key || !isVisible) {
      return null;
    }

    const candidateOutreachDialogs = state.dialogs[dialogType];

    const dialogData = candidateOutreachDialogs ? candidateOutreachDialogs[key] ?? null : null;

    return dialogData;
  });

  return useMemo(() => {
    if (!dialogData) return null;

    if (selector) {
      const propData = selector(dialogData);

      return propData;
    }

    return dialogData;
  }, [dialogData, selector]);
}
